import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'
import { useAlertContext } from '@/contexts/AlertContext'
import type {
  BatteryBinding,
  Resource,
  ResourceConfig,
  ResourceType,
} from '@/features/controller/configuration/models/resourceConfigSchema'
import { BindingForm } from '@/features/resource/components/localControl/forms/BindingForm'
import { KeyValueGrid } from '@/features/resource/components/localControl/KeyValueGrid'
import type { BindingFormSchemaType } from '@/features/resource/components/localControl/schemas'
import { SectionHeader } from '@/features/resource/components/localControl/SectionHeader'
import { useEditSection } from '@/features/resource/components/localControl/useEditSection'
import { useUpdateLocalControlConfigMutation } from '@/features/resource/components/localControl/useUpdateLocalControlConfigMutation'

/**
 * Context required to enable editing of this section. When omitted, the panel is read-only.
 * `ResourceJsonEditor` builds this context from the loaded `ConfigurationVersion` + parsed
 * `resource.json` document and passes it in; standalone embeds (e.g. tests) can omit it to
 * render the panel as a static read-only view.
 */
export type BindingPanelEditContext = Readonly<{
  controllerId: string
  resource: Resource
  fullConfig: ResourceConfig
  previousVersionId: string
}>

type BindingPanelProps = Readonly<{
  resourceId: string
  resourceType: ResourceType
  binding: BatteryBinding
  editContext?: BindingPanelEditContext
}>

export function BindingPanel({ resourceId, resourceType, binding, editContext }: BindingPanelProps) {
  const { t } = useTranslation()
  const { state, startEdit, cancel, startSave, saveSuccess, saveError } = useEditSection()
  const { pushAlert } = useAlertContext()
  const mutation = useUpdateLocalControlConfigMutation(editContext?.controllerId ?? '')

  const items = [
    { label: t('resources.local_control.binding.id'), value: resourceId },
    {
      label: t('resources.local_control.binding.type'),
      value: t(`resources.local_control.resource_type.${resourceType}`),
    },
    { label: t('resources.local_control.binding.battery_ref'), value: binding.batteryRef },
  ]

  async function handleSave(data: BindingFormSchemaType) {
    if (!editContext) return
    startSave()
    try {
      await mutation.updateLocalControlConfig({
        previousVersionId: editContext.previousVersionId,
        currentConfig: editContext.fullConfig,
        updatedResource: {
          ...editContext.resource,
          // Spread the EXISTING binding first so any on-disk keys we no longer surface (notably
          // `meterRef`, which LC-168 confirmed isn't needed for onboarding but the schema still
          // allows) round-trip verbatim through the save. Only the form-edited `batteryRef` gets
          // overlaid.
          binding: { ...editContext.resource.binding, batteryRef: data.batteryRef },
        },
      })
      saveSuccess()
      pushAlert({ message: t('resources.local_control.binding.save_success'), severity: 'success' })
    } catch {
      saveError()
      pushAlert({ message: t('resources.local_control.binding.save_error'), severity: 'error' })
    }
  }

  const isEditing = state === 'edit' || state === 'saving'
  const canEdit = editContext !== undefined

  return (
    <Stack gap={1.5}>
      <Stack alignItems="flex-start" direction="row" justifyContent="space-between">
        <SectionHeader title={t('resources.local_control.binding.title')} />
        {canEdit && !isEditing && (
          <CustomButton
            aria-label={t('resources.local_control.binding.edit_label')}
            size="small"
            startIcon={<EditOutlinedIcon />}
            variant="text"
            onClick={startEdit}
          >
            {t('common.button.edit')}
          </CustomButton>
        )}
      </Stack>
      {isEditing ? (
        <BindingForm binding={binding} isSaving={state === 'saving'} onCancel={cancel} onSave={handleSave} />
      ) : (
        <KeyValueGrid columns={2} items={items} />
      )}
    </Stack>
  )
}
